import { useCallback, useRef } from 'react';
import { delay } from '@glomopay/utils';

// Structural rather than `ILogger` from telemetry: each app owns its own configured logger, and
// this package should not acquire a telemetry dependency to accept one.
type RetryableImportLogger = {
  warn: (message: string, attributes?: Record<string, string | number | boolean>) => void;
};

interface RetryableModuleImportOptions {
  maxAttempts?: number;
  baseDelaySeconds?: number;
  label?: string;
  logger?: RetryableImportLogger;
}

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY_SECONDS = 0.5;

/**
 * A failed dynamic import is cached by the browser's module map against the exact URL, so retries
 * append a unique param to force a real re-fetch rather than replay the rejection.
 *
 * Resolves to `null` once attempts are exhausted; per-attempt failures only warn, since a single
 * blip is expected-transient and the caller decides how to surface the terminal `null`.
 */
const useRetryableModuleImport = <T = unknown>(url: string, options: RetryableModuleImportOptions = {}) => {
  const { maxAttempts = DEFAULT_MAX_ATTEMPTS, baseDelaySeconds = DEFAULT_BASE_DELAY_SECONDS, label = 'module', logger } = options;
  const importCountRef = useRef(0);

  return useCallback(async (): Promise<T | null> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const importUrl = importCountRef.current === 0 ? url : `${url}${url.includes('?') ? '&' : '?'}cb=${Date.now()}-${importCountRef.current}`;
        importCountRef.current += 1;
        return (await import(/* @vite-ignore */ importUrl)) as T;
      } catch (error) {
        logger?.warn('Dynamic import attempt failed', {
          module: label,
          attempt,
          maxAttempts,
          error: error instanceof Error ? error.message : String(error),
        });
        if (attempt < maxAttempts) {
          await delay(baseDelaySeconds * attempt);
        }
      }
    }
    return null;
  }, [url, maxAttempts, baseDelaySeconds, label, logger]);
};

export default useRetryableModuleImport;
